/* STEP 7e-2 · S2 — the firm's methodology settings on the worksheets (GET /engagements/{id}/methodology-settings: the CONFIRMED
   settings of the version the engagement is linked to; ``applies`` false = no settings → nothing is shown, behaviour as before).
   105(a): the firm range next to the benchmark %, PM % and CT % (the fields are not prefilled); a % outside its range shows a
   reason input — sent as ``methodology_reasons`` {field: reason}; the server refuses the save without it.
   Sampling: the confidence level starts at the firm minimum and the expected misstatement at the firm default % of the tolerable
   misstatement (once, when the field is still empty); a level below the minimum shows a reason input (``methodology_reason``). */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { Input } from "@/components/ui/input";
import { FieldMessage } from "@/lib/useMissingFields";
import { fmtSetting, fmtPct } from "@/components/methodology/MethodologySettingsDialog";

const EPS = 1e-9;

export function useEngagementMethodology(engagementId) {
  const [ms, setMs] = useState(null);
  const [devs, setDevs] = useState([]);
  const reload = useCallback(() => {
    if (!engagementId) { setMs(null); setDevs([]); return; }
    api.get(`/engagements/${engagementId}/methodology-settings`, { _silent: true }).then((r) => setMs(r.data)).catch(() => setMs(null));
    api.get(`/engagements/${engagementId}/methodology-deviations`, { _silent: true })
      .then((r) => setDevs(asArray(r.data?.rows))).catch(() => setDevs([]));
  }, [engagementId]);
  useEffect(() => { reload(); }, [reload]);
  return { ms, devs, reload };
}

export function matSetting(field, doc, ms) {
  if (field === "pct_applied") return (ms?.benchmark_field || {})[doc?.benchmark || ""] || null;
  return field;
}

export function isOutside(setting, firm, value) {
  if (firm == null || !(value > 0)) return false;
  if (typeof firm === "object") return value < Number(firm.min) - EPS || value > Number(firm.max) + EPS;
  return value < Number(firm) - EPS;
}

function openDeviation(devs, field, value, ms) {
  return asArray(devs).find((d) => d.area === "materiality" && d.field === field && d.status === "open"
    && d.methodology_version_id === ms?.version?.id && Number(d.engagement_value) === value);
}

/** The 105(a) fields outside the firm range → {field: reason} (the typed reason, else the open deviation's own reason). */
export function matReasonsBody(ms, devs, doc, reasons) {
  if (!ms?.applies) return undefined;
  const out = {};
  for (const field of ["pct_applied", "pm_pct", "ct_pct"]) {
    const setting = matSetting(field, doc, ms);
    const value = Number(doc?.[field] || 0);
    if (!setting || !isOutside(setting, ms.settings?.[setting], value)) continue;
    const r = (reasons?.[field] ?? openDeviation(devs, field, value, ms)?.reason ?? "").trim();
    if (r) out[field] = r;
  }
  return Object.keys(out).length ? out : undefined;
}

export function MatRangeNote({ ms, devs, field, doc, reasons, setReasons, mf, t }) {
  if (!ms?.applies) return null;
  const setting = matSetting(field, doc, ms);
  const firm = setting ? ms.settings?.[setting] : null;
  if (firm == null) return null;
  const value = Number(doc?.[field] || 0);
  const out = isOutside(setting, firm, value);
  const key = `methodology_reason_${field}`;
  const od = openDeviation(devs, field, value, ms);
  const val = reasons?.[field] ?? od?.reason ?? "";
  return (
    <div className="mt-1 space-y-1" data-testid={`mat-meth-${field}`}>
      <div className={`text-[10px] ${out ? "font-medium text-amber-800 dark:text-amber-300" : "text-muted-foreground"}`} data-testid={`mat-meth-range-${field}`}>
        {t("s7e2.range.firm").replace("{r}", fmtSetting("range", firm))} · {t("s7e2.range.version").replace("{n}", ms.version?.version ?? "")}
      </div>
      {out && (
        <div>
          <div className="text-[10px] text-amber-800 dark:text-amber-300">{t("s7e2.range.outside")}</div>
          <Input className="h-7 text-[12px]" value={val} placeholder={t("s7e2.range.reasonPh")}
                 onChange={(e) => { setReasons({ ...(reasons || {}), [field]: e.target.value }); if (mf) mf.filled(key, e.target.value); }}
                 {...(mf ? mf.props(key) : {})} data-testid={`mat-meth-reason-${field}`} />
          {mf && <FieldMessage mf={mf} field={key} testid={`mat-meth-reason-missing-${field}`} />}
        </div>
      )}
    </div>
  );
}

/** Sampling: start the confidence at the firm minimum and the expected misstatement at the firm default (once each). */
export function useSamplingDefaults(ms, form, setForm, tolerable) {
  const done = useRef({ conf: false, em: false });
  const min = ms?.applies ? ms.settings?.sampling_min_confidence : null;
  const emPct = ms?.applies ? ms.settings?.expected_misstatement_pct : null;
  useEffect(() => {
    if (min == null || done.current.conf) return;
    done.current.conf = true;
    if (form.confidence_pct === "" || form.confidence_pct == null) setForm((f) => ({ ...f, confidence_pct: String(min) }));
  }, [min]);   // eslint-disable-line react-hooks/exhaustive-deps
  const tm = Number(form.tolerable_misstatement !== "" && form.tolerable_misstatement != null ? form.tolerable_misstatement : tolerable) || 0;
  useEffect(() => {
    if (emPct == null || done.current.em || !(tm > 0)) return;
    done.current.em = true;
    if (form.expected_misstatement === "" || form.expected_misstatement == null) {
      setForm((f) => ({ ...f, expected_misstatement: String(Math.round(tm * emPct) / 100) }));
    }
  }, [emPct, tm]);   // eslint-disable-line react-hooks/exhaustive-deps
}

export function SamplingConfidenceNote({ ms, form, reason, setReason, mf, t }) {
  const min = ms?.applies ? ms.settings?.sampling_min_confidence : null;
  if (min == null) return null;
  const value = Number(form.confidence_pct || 0);
  const below = isOutside("sampling_min_confidence", min, value);
  return (
    <div className="space-y-1" data-testid="samp-meth">
      <div className={`text-[10px] ${below ? "font-medium text-amber-800 dark:text-amber-300" : "text-muted-foreground"}`} data-testid="samp-meth-min">
        {t("s7e2.range.firmMin").replace("{r}", fmtPct(min))} · {t("s7e2.range.version").replace("{n}", ms.version?.version ?? "")}
      </div>
      {below && (
        <div>
          <div className="text-[10px] text-amber-800 dark:text-amber-300">{t("s7e2.range.below")}</div>
          <Input className="h-7 text-[12px]" value={reason || ""} placeholder={t("s7e2.range.reasonPh")}
                 onChange={(e) => { setReason(e.target.value); if (mf) mf.filled("methodology_reason", e.target.value); }}
                 {...(mf ? mf.props("methodology_reason") : {})} data-testid="samp-meth-reason" />
          {mf && <FieldMessage mf={mf} field="methodology_reason" testid="samp-meth-reason-missing" />}
        </div>
      )}
    </div>
  );
}

export function SamplingEmNote({ ms, t }) {
  const pct = ms?.applies ? ms.settings?.expected_misstatement_pct : null;
  if (pct == null) return null;
  return (
    <div className="mt-0.5 text-[10px] text-muted-foreground" data-testid="samp-meth-em">
      {t("s7e2.range.firmDefault").replace("{r}", fmtPct(pct))} · {t("s7e2.range.version").replace("{n}", ms.version?.version ?? "")}
    </div>
  );
}

/** The reason to send with POST /samples (only when the level is below the firm minimum). */
export function samplingReasonBody(ms, form, reason) {
  const min = ms?.applies ? ms.settings?.sampling_min_confidence : null;
  if (min == null || !isOutside("sampling_min_confidence", min, Number(form.confidence_pct || 0))) return undefined;
  return (reason || "").trim() || undefined;
}
